import { useMemo, useState, useEffect } from 'react';
import { v4 } from 'uuid';
import { useLocalization } from '../Provider/Provider.js';
import { useProxiedRef } from '../../utils/hooks/useProxiedRef.js';
import { getIndexFromValue, getSelectedIndexesFromValue, setInputValueByRef, getActiveDescendant, findByValue } from './util.js';
import { useTypeahead } from './useTypeahead.js';

const useMultiListbox = ({
  data: externalData = [],
  defaultValue,
  disabled,
  emptyValue: _,
  id: nativeId,
  name,
  onClick,
  onChange,
  onFocus,
  onKeyDown,
  value,
  ...otherProps
}, ref) => {
  const {
    texts
  } = useLocalization();
  const id = useMemo(() => nativeId || v4(), [nativeId]);
  const inputRef = useProxiedRef(ref);
  const data = useMemo(() => {
    return [{
      text: texts.listbox.allOption,
      value: '#ALL-OPTIONS#'
    }, ...externalData];
  }, [externalData]);
  const [currentIndex, setCurrentIndex] = useState(getIndexFromValue(data, value) || 0);
  const [allOptionsSelected, setAllOptionsSelected] = useState(false);
  const {
    getNextIndex
  } = useTypeahead({
    data,
    currentIndex
  });
  const [selectedIndexes, setSelectedIndexes] = useState(getSelectedIndexesFromValue(data, value));
  useEffect(() => {
    if (defaultValue && !value) {
      setInputValueByRef(inputRef.current, defaultValue.toString());
    }
  }, []);
  useEffect(() => {
    const selectableItemsCount = data.filter(item => !item.disabled).length - 1;

    if (selectableItemsCount === selectedIndexes.length) {
      setAllOptionsSelected(true);
    } else {
      setAllOptionsSelected(false);
    }
  }, [selectedIndexes, data]);

  const setInputValueByIndex = index => {
    if (index !== undefined) {
      const option = data[index];

      if (option && !option.disabled) {
        var _inputRef$current;

        let newInputValue = '';
        const currentInputValue = (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.value;
        const currentValuesArray = (currentInputValue === null || currentInputValue === void 0 ? void 0 : currentInputValue.split(',')) || [];
        const optionAlreadySelected = currentValuesArray.findIndex(val => val === String(option.value)) !== -1; // Handling the select_all option

        if (option.value === '#ALL-OPTIONS#') {
          if (!allOptionsSelected) {
            newInputValue = data.filter((option, index) => index !== 0 && !option.disabled).map(option => option.value).join(',');
          } else {
            newInputValue = '';
          }
        } else {
          // Handling rest options
          if (optionAlreadySelected) {
            newInputValue = currentValuesArray.filter(val => val !== String(option.value)).join(',');
          } else {
            newInputValue = currentInputValue ? `${currentInputValue},${option.value}` : option.value;
          }
        }

        setInputValueByRef(inputRef.current, newInputValue);
      }
    }
  };

  const handleListboxClick = (event, index) => {
    event.preventDefault();

    if (!data[index].disabled) {
      setCurrentIndex(index);
      setInputValueByIndex(index);
    }

    if (onClick) {
      onClick(event);
    }
  };

  const handleListboxKeyDown = (event, index) => {
    const charKey = event.key; // event.key always returns the whole string (String.fromCharCode(event.keyCode) does not)
    // We need to filter out only alphabetical or numeric letters, that's why that condition has been changed

    if (charKey.length === 1 && /(\w)/g.test(charKey)) {
      const nextIndex = getNextIndex(charKey);

      if (nextIndex > -1 && nextIndex !== currentIndex) {
        setCurrentIndex(nextIndex);
      }

      return;
    }

    switch (event.key) {
      case ' ':
        {
          event.preventDefault();
          setInputValueByIndex(index);
          break;
        }

      case 'Enter':
        {
          setInputValueByIndex(index);
          break;
        }
    }

    setCurrentIndex(index !== undefined ? index : 0);

    if (onKeyDown) {
      event.persist();
      onKeyDown(event);
    }
  };

  const handleListboxFocus = event => {
    if (onFocus) {
      event.persist();
      onFocus(event);
    }
  };

  const handleInputChange = event => {
    event.persist();
    setSelectedIndexes(getSelectedIndexesFromValue(data, event.target.value));

    if (onChange) {
      const detail = [];
      const valuesArray = event.target.value.split(',');
      valuesArray.forEach(val => {
        const item = findByValue(data, val);

        if (item) {
          detail.push(item);
        }
      });
      event.detail = detail;
      onChange(event);
    }
  };

  const list = { ...otherProps,
    'aria-activedescendant': getActiveDescendant(data, currentIndex, id),
    'aria-multiselectable': true,
    data,
    disabled,
    id,
    onChange: () => null,
    onClick: handleListboxClick,
    onFocus: handleListboxFocus,
    onKeyDown: handleListboxKeyDown,
    scrollOnFocus: false,
    tabIndex: disabled ? -1 : otherProps.tabIndex ? otherProps.tabIndex : 0,
    value: currentIndex,
    selectedIndexes,
    multiselect: true,
    allOptionsSelected
  };
  const input = {
    name,
    onChange: handleInputChange,
    ref: inputRef,
    tabIndex: -1,
    value: value !== null && value !== void 0 ? value : ''
  };
  return {
    list,
    input
  };
};

export { useMultiListbox };
//# sourceMappingURL=useMultiListbox.js.map
